
import { v4 as uuidv4 }                 from 'uuid';

import {ProjectElement}                 from './project.element';
import {ProjectElementType}             from './project.element.type';
import {Project}                        from '../project';

import {LocomotiveDataHandle}           from "@/models/project/locomotive.data.handle";
import {FAVRecord}                      from "@/models/project/f.a.v.record";

import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";
import {Logger}                         from "@/util/logger";
import {PropertiesHolder}               from "@/models/properties.holder";
import {BlobType}                            from "@/models/blob";
import {TaskProgressService}                 from "@/services/task.progress.service";
import {TaskUpdate}                          from "@/models/task.update";
import {TaskLocomotiveModelCreate}           from "@/models/task.locomotive.model.create";
import {TaskLocomotiveModelCreateComplete}   from "@/models/task.locomotive.model.create.complete";
import {TaskLocomotiveModelCreateFailed}     from "@/models/task.locomotive.model.create.failed";
import {TaskLocomotiveModelDownload}         from "@/models/task.locomotive.model.download";
import {TaskLocomotiveModelDownloadFailed}   from "@/models/task.locomotive.model.download.failed";
import {TaskLocomotiveModelDownloadComplete} from "@/models/task.locomotive.model.download.complete";
import {catchError, tap} from "rxjs/operators";
import {isset} from "@/util/util";
import {TaskUpdateFileUploadFailed} from "@/models/task.update.file.upload.failed";
import {forkJoin, of} from "rxjs";
import { HttpEvent, HttpEventType } from "@angular/common/http";
import {TaskUpdateFileUpload} from "@/models/task.update.file.upload";
import {TaskUpdateFileUploadComplete} from "@/models/task.update.file.upload.complete";
import {FileBrowserMetadata} from "@/screens/ide/file.browser.metadata";

/**
 *
 * @class LocomotiveModelResultElement
 *
 * Node that maps directly to a locomotive model result that was created over in BigQuery
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2022
 *
 */
@ProjectElementFactoryDecorator('LocomotiveModelResultElement')
export class LocomotiveModelResultElement extends ProjectElement {

  protected actionKeys: string[] = [
    'download',
    'deleteresult',
    'viewinbrowser'
  ];

  /** the (remote: BigQuery) model data result */
  public _handle: LocomotiveDataHandle = null;

  /** the FAV we used as input to the model */
  public _fav: FAVRecord = null;

  /** the model name */
  public _name: string = '';

  /** the start date for the model */
  public _start: string = '';

  /** the end date for the model */
  public _end: string = '';

  /** standard constructor */
  constructor(
    project: Project,
    id: string,
    handle: LocomotiveDataHandle,
    fav: FAVRecord,
    name: string,
    start: string,
    end: string,
    parent: ProjectElement,
    publish?: boolean,
    peType?: ProjectElementType
  ) {

    super(project, id, peType ? peType : ProjectElementType.LocoMotiveModelResult);

    this._handle      = handle;
    this._fav         = fav;
    this._name        = name;
    this._start       = start;
    this._end         = end;
    this._label       = `${this._name}: ${this._start} >> ${this._end}`;

    this._enabled     = true;
    this._icon        = 'fas fa-database';
    this._elementKind = 'LocoModelResult';

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring LocomotiveModelResultElement");
  }

  /** drop data on this element */
  public acceptDrop(data): boolean {

    return false;
  }

  /** is this element deletable? */
  public isDeletable() : boolean {

    const kids: ProjectElement[] = this.children();

    return kids.length <= 0;
  }

  /** test if any blob of this type can be a child of this element */
  public acceptsBlobType(type: BlobType): boolean {

    return false;
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({
      name:   this._name,
      start:  this._start,
      end:    this._end,
      handle: this._handle,
      fav:    this._fav
    });
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any) {

    /* recover the basics */

    super.fromNode(node);

    /* recover the model details */

    this._name   = node.name;
    this._start  = node.start;
    this._end    = node.end;
    this._handle = new LocomotiveDataHandle(node.handle);
    this._fav    = new FAVRecord(node.fav);

  }


  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.download = {
      name: 'Download Result Table',
      icon: 'fas fa-file-download',
      disabled: false
    };

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    actionSet.deleteresult = {
      name: 'Delete Model',
      icon: 'fas fa-trash',
      disabled: false
    };

    return actionSet;
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.actionKeys.find(x => x === key);
  }

  /** sub-classes must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    let pe: LocomotiveModelResultElement = this;

    if ( !pe.actionKeys.find(x => x === key) ) {

      /* let ProjectElement handle it */

      pe._parent.action(key, selection);

      return ;
    }

    switch (key) {

      case 'download': {

          /*
           * on doing Angular requests with progress:
           *
           *   https://nils-mehlhorn.de/posts/angular-file-download-progress
           *   https://angular.io/api/common/http/HttpProgressEvent
           */

          const tasks: TaskProgressService = pe._project.services.taskMonitor;
          const taskId: string             = TaskUpdate.generateId();
          const name: string               = pe._name;
          const csvFile: string            = `${name}.csv`;
          let table: string                = `${pe._handle.id.replace(':', '.')}.locomotive_forecast_details`;
          let query: string                = `SELECT * FROM \`${table}\` ORDER BY ID ASC;`;

          /* figure out the table to dump ... */

          Logger.debug(`[LocomotiveModelResultElement] Downloading (${table}), SQL: ${query}`);

          tasks.progress(new TaskLocomotiveModelDownload(taskId, `Building data for ${csvFile}, please wait...`, 1.0));

          /* run the query that will dump all the rows, and save that query result to an anonymous table */

          pe._project.services.bigquery.runQuery(query).then(
            (anonTable: string) => {

              /* first of four steps complete, set progress to 25 */

              /* now with an anonymous (temporary) table in hand, we can extract / export it to Google storage... */

              tasks.progress(new TaskLocomotiveModelDownload(taskId, `Data ready, extracting to storage, please wait ...`, 25.0));

              Logger.debug(`[LocomotiveModelResultElement] anonymous table is ready: ${anonTable}`);

              /* extract ... */

              const uuid: string = uuidv4();
              const dst:  string = `gs://cn-dmz/extractions/${uuid}/${csvFile}`;

              pe._project.services.bigquery.extract(anonTable, dst).then(
                (csvURL: string) => {

                  /*
                   * now that its on Google storage, just fetch it (throughout backend), let the backend
                   * auto-clean the storage, while we pass the file through to the user, to download to their
                   * desktop...
                   *
                   */

                  /* second of four steps complete, set progress to 50 */

                  tasks.progress(new TaskLocomotiveModelDownload(taskId, `Extracted: ${csvURL}, Downloading ...`, 50.0));

                  /*
                   * transfer the file from our own backend (the progress manager Docker container) to
                   * the web browser.  As we do that, capture the download progress events so we can
                   * keep showing the user progress.
                   *
                   */

                  let request = pe._project.services.storage.get(csvURL).pipe(

                    catchError((err) => {

                      let message = err;

                      if (isset(err.statusText)) {

                        message = err.statusText;
                      }

                      if (isset(err.error.message)) {

                        message = err.error.message;
                      }

                      Logger.debug(`[LocomotiveModelResultElement]  ERROR ${csvURL}:`, message);

                      tasks.progress(new TaskLocomotiveModelDownloadFailed(taskId, message));

                      return of(null);

                    }));

                  forkJoin([request].map(

                    o => o.pipe(

                      tap((e: HttpEvent<any>) => {

                        if (!e) {

                          return;
                        }

                        if ((e.type == HttpEventType.DownloadProgress)||(e.type == HttpEventType.UploadProgress)) {

                          /*
                           *  {
                           *    type: HttpEventType.DownloadProgress | HttpEventType.UploadProgress
                           *    loaded: number
                           *    total?: number
                           *  }
                           *
                           */

                          /*
                           * download progress, this is the 3rd step, but, once the data is all in the
                           * the browser or its own local file storage, when the user does a "Save As"...
                           * it will basically be instant. So, do the 3rd and 4th step progress here.
                           *
                           */

                          const progress: number = 50.0 + (50.0 *  (e.loaded / e.total));

                          Logger.debug(`[LocomotiveModelResultElement] D progress (${progress}): `, e);

                          tasks.progress(new TaskLocomotiveModelDownload(taskId, `Extracted: ${csvURL}, Downloading ...`, progress));

                        } else if (e.type == HttpEventType.Response) {

                          /* the request was sent */

                        } else if (e.type == HttpEventType.ResponseHeader) {

                          /*
                           * {
                           *    headers: ...
                           *    ok: true
                           *    status: 200
                           *    statusText: "OK"
                           *    type: 2
                           *    url: "http://127.0.0.1:5004/backend/storage/...
                           * }
                           *
                           */

                          /* we got a response back, this means the download was completed. */

                          Logger.debug(`[LocomotiveModelResultElement] D complete mark: `, e);

                        }
                      })

                    ))).subscribe(

                    (events: any[]) => {

                      const downloadable: Blob = events[0].body;

                      Logger.debug(`[LocomotiveModelResultElement] download is for real complete.`, downloadable);

                      /* now just stream the file out to the user, from the browser for download ... */

                      const url  = window.URL.createObjectURL(downloadable);

                      /* setup a fake one-time link with the right download name and the blob attached as the download */

                      const element: HTMLAnchorElement = document.createElement('a');
                      element.href          = url;
                      element.download      = csvFile;

                      element.style.display = 'none';

                      document.body.appendChild(element);

                      /* do it */

                      element.click();

                      /* clean up */

                      document.body.removeChild(element);

                      Logger.debug(`[LocomotiveModelResultElement] download completed.`);

                      tasks.progress(new TaskLocomotiveModelDownloadComplete(taskId, `${csvFile} downloaded.`));
                    },
                    (error) => {

                      /* track the error */

                      tasks.progress(new TaskLocomotiveModelDownloadFailed(taskId, error));

                      /* log the error */

                      Logger.error(`[LocomotiveModelResultElement] Problem transferring from backend: `, pe._handle);
                    });

                },
                (error) => {

                  /* track the error */

                  tasks.progress(new TaskLocomotiveModelDownloadFailed(taskId, error));

                  /* log the error */

                  Logger.error(`[LocomotiveModelResultElement] Problem extracting table data for: `, pe._handle);

                });

            },
            (error) => {

              /* track the error */

              tasks.progress(new TaskLocomotiveModelDownloadFailed(taskId, error));

              /* log the error */

              Logger.error(`[LocomotiveModelResultElement] Problem downloading model for: `, pe._handle);

            });

        }
        break;

      case 'viewinbrowser': {

        this._project.services.ide.launchWindow(FileBrowserMetadata.build(this._blobKey, this._blobKey));

        break;
      }

      case 'deleteresult': {

          Logger.debug(`[LocomotiveModelResultElement] Deleting for: `, pe._handle);

          /* delete the remove model (clean up in Big Query */

          /* track delete start */

          const tasks: TaskProgressService = pe._project.services.taskMonitor;
          const taskId: string = TaskUpdate.generateId();
          const name: string   = pe._name;

          tasks.progress(new TaskLocomotiveModelCreate(taskId, `Deleting: ${name} ...`, 1.0));

          pe._project.services.locoquery.locoDelete(pe._handle.id).then(
            (result: string) => {

              /* track delete model done */

              tasks.progress(new TaskLocomotiveModelCreate(taskId, `Model deleted, saving...`, 99.0));

              Logger.debug(`[LocomotiveModelResultElement] model deleted for: `, pe._handle);

              /* delete self */

              pe.deleteElements([pe]);

              tasks.progress(new TaskLocomotiveModelCreateComplete(taskId, `Deleted ${name}.`));
            },
            (error) => {

              /* track the error */

              tasks.progress(new TaskLocomotiveModelCreateFailed(taskId, error));

              /* log the error */

              Logger.error(`[LocomotiveModelResultElement] Problem deleting model for: `, pe._handle);
            });
        }
        break;
    }
  }

  /** append these elements properties to the holder */
  public properties(properties?: PropertiesHolder): PropertiesHolder {

    if (!properties) {

      properties = new PropertiesHolder();

    }

    /* add properties unique to a loco model */

    properties.set('Start',      this._start);
    properties.set('End',        this._end);
    properties.set('Dataset ID', this._handle.id);
    properties.set('Forecast',   this._handle.forecast);
    properties.set('Created',    this._handle.created);
    properties.set('Row Count',  this._handle.total.toString());
    properties.set('BigQuery',   this._handle.query);

    /* add in the usual properties */

    return super.properties(properties);
  }
}
